import { v4 as uuidv4 } from 'uuid'
import * as v from 'valibot'
import { differenceInMinutes } from '@chilipiper/date-time'
import {
  getPrepareChatDataKey,
  getStoredSessionKey,
  getWidgetGuestIdStorageKey,
  getWidgetStateKey,
  storage,
} from './storage'

export const syncStoredGuestId = (
  workspaceId: string,
  tenantId: string,
  forcePlaybookId?: string
): string => {
  const guestIdKey = getWidgetGuestIdStorageKey(workspaceId, tenantId)
  const storageGuestId = storage.getItem(guestIdKey)

  if (storageGuestId) {
    const storedSession = getStoredSession(storageGuestId, workspaceId)
    if (
      !forcePlaybookId ||
      !storedSession?.playbookId ||
      storedSession.playbookId === forcePlaybookId
    ) {
      return storageGuestId
    }
  }

  const guestId: string = uuidv4()
  storage.setItem(guestIdKey, guestId)
  return guestId
}

export const getWidgetState = (guestId: string) => {
  const widgetStateKey = getWidgetStateKey(guestId)
  return storage.getItem(widgetStateKey) as 'preview' | 'collapsed' | 'expanded' | null
}

export const setWidgetState = (guestId: string, state: 'preview' | 'collapsed' | 'expanded') => {
  const widgetStateKey = getWidgetStateKey(guestId)
  storage.setItem(widgetStateKey, state)
  return state
}

export const setPrepareChatData = (
  guestId: string,
  workspaceId: string,
  data: {
    authorizationHeader: string
    guestJid: string
    refreshToken?: string
    roomJid: string
  }
) => {
  const prepareChatDataKey = getPrepareChatDataKey(guestId, workspaceId)
  storage.setItem(prepareChatDataKey, JSON.stringify(data))
}

const PrepareChatDataSchema = v.object({
  authorizationHeader: v.string(),
  guestJid: v.string(),
  refreshToken: v.optional(v.string()),
  roomJid: v.string(),
})

export const getPrepareChatData = (guestId: string, workspaceId: string) => {
  const prepareChatDataKey = getPrepareChatDataKey(guestId, workspaceId)
  const storedValue = storage.getItem(prepareChatDataKey)

  const data = v.safeParse(PrepareChatDataSchema, JSON.parse(storedValue ?? '{}'))
  if (!data.success) {
    return undefined
  }
  return data.output
}

const SessionSchema = v.object({
  timestamp: v.string(),
  playbookId: v.string(),
})

export const getStoredSession = (guestId: string, workspaceId: string) => {
  const key = getStoredSessionKey(guestId, workspaceId)
  const storedValue = storage.getItem(key)

  const item = v.safeParse(SessionSchema, JSON.parse(storedValue ?? '{}'))
  if (!item.success) {
    return undefined
  }
  return {
    hasActiveSession: differenceInMinutes(new Date(), new Date(item.output.timestamp)) < 30,
    playbookId: item.output.playbookId,
  }
}

export const setStoredSession = (
  guestId: string,
  workspaceId: string,
  timestamp: string,
  playbookId: string
) => {
  const key = getStoredSessionKey(guestId, workspaceId)
  storage.setItem(key, JSON.stringify({ timestamp, playbookId }))
}
